import React from 'react';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';

import type {
  Supplier,
  SupplierBlockingCategory,
  SupplierBlockingCondition,
} from '../api';
import { formatCivilDate, formatLocalDateTime } from '../utils/dates';

const categoryLabels: Record<SupplierBlockingCategory, string> = {
  document_pending: 'Pendência documental',
  homologation_pending: 'Pendência de homologação',
  rejection: 'Reprovação',
  expired_document: 'Documento vencido',
  blocking_risk: 'Risco impeditivo',
  registration_pending: 'Pendência cadastral',
  administrative_block: 'Bloqueio administrativo',
  homologation_expired: 'Homologação expirada',
  other: 'Outra condição impeditiva',
};

const actionLabels: Record<string, string> = {
  update_registration: 'Atualizar o cadastro do fornecedor.',
  upload_document: 'Enviar o documento solicitado.',
  replace_document: 'Enviar uma nova versão do documento.',
  update_document: 'Atualizar o documento.',
  review_document: 'Concluir a análise do documento.',
  complete_homologation_step: 'Concluir a etapa pendente da homologação.',
  review_homologation_step: 'Revisar a decisão da etapa de homologação.',
  review_risk: 'Revisar o risco impeditivo.',
  renew_homologation: 'Renovar a homologação.',
  remove_administrative_block: 'Remover o bloqueio administrativo após a regularização.',
  reactivate_supplier: 'Reativar o fornecedor.',
};

const actionLabel = (action: string) =>
  actionLabels[action] || action.replaceAll('_', ' ');

const inconsistencyCondition: SupplierBlockingCondition = {
  code: 'missing_blocking_cause',
  category: 'other',
  message: 'Inconsistência: a compra está bloqueada sem causa registrada.',
  action: 'Solicite a revisão do status consolidado do fornecedor.',
};

export function getSupplierBlockingConditions(
  supplier: Pick<Supplier, 'purchaseEligible' | 'blockingConditions' | 'blockingReasons'>,
): SupplierBlockingCondition[] {
  const structured = supplier.blockingConditions ?? [];
  if (structured.length > 0) return structured;

  const legacy = (supplier.blockingReasons ?? []).map((message, index) => ({
    code: `legacy_${index}`,
    category: 'other' as const,
    message,
  }));
  if (legacy.length > 0) return legacy;
  return supplier.purchaseEligible ? [] : [inconsistencyCondition];
}

export const SupplierBlockingSummary: React.FC<{ supplier: Supplier }> = ({ supplier }) => {
  const conditions = getSupplierBlockingConditions(supplier);
  if (supplier.purchaseEligible) {
    return <span className="inline-flex rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700">Liberado</span>;
  }

  return (
    <div className="max-w-[260px]">
      <span className="inline-flex rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-700">Bloqueado</span>
      {conditions.length === 1 ? (
        <div className="mt-1 text-xs text-red-700">
          <span className="font-semibold">{categoryLabels[conditions[0].category]}:</span>{' '}
          {conditions[0].message}
        </div>
      ) : (
        <details className="mt-1 text-xs text-red-700">
          <summary className="cursor-pointer font-semibold focus:outline-none focus:ring-2 focus:ring-red-500">
            {conditions.length} causas ativas
          </summary>
          <ul className="mt-1 list-disc space-y-1 pl-4">
            {conditions.map((condition) => (
              <li key={`${condition.code}-${condition.sourceId ?? ''}`}>
                <span className="font-semibold">{categoryLabels[condition.category]}:</span>{' '}
                {condition.message}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};

export const SupplierBlockingConditionsPanel: React.FC<{
  supplier: Supplier;
  title?: string;
  showEligible?: boolean;
}> = ({ supplier, title = 'Causas ativas e regularização', showEligible = false }) => {
  const titleId = React.useId();
  const conditions = getSupplierBlockingConditions(supplier);
  if (conditions.length === 0) {
    if (!showEligible) return null;
    return (
      <div
        className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800"
        role="status"
        aria-live="polite"
      >
        <div className="flex items-center gap-2 font-semibold"><CheckCircle2 className="h-4 w-4" /> Sem condições impeditivas</div>
      </div>
    );
  }

  return (
    <section
      className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
      aria-labelledby={titleId}
      aria-live="polite"
      aria-atomic="true"
    >
      <h3 id={titleId} className="mb-2 flex items-center gap-2 font-semibold">
        <AlertTriangle className="h-4 w-4 shrink-0" /> {title}
      </h3>
      <ul className="space-y-2">
        {conditions.map((condition) => (
          <li key={`${condition.code}-${condition.sourceId ?? ''}`} className="border-t border-amber-200 pt-2 first:border-0 first:pt-0">
            <div className="font-medium">{categoryLabels[condition.category]}</div>
            <div>{condition.message}</div>
            {condition.action && <div className="mt-1 text-xs"><span className="font-semibold">Como regularizar:</span> {actionLabel(condition.action)}</div>}
          </li>
        ))}
      </ul>
    </section>
  );
};

export const SupplierHomologationMetadata: React.FC<{ supplier: Supplier }> = ({ supplier }) => (
  <section className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700" aria-label="Metadados da homologação">
    <div className="font-semibold text-gray-900">Metadados da homologação</div>
    <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-2">
      <div><dt className="text-gray-500">Homologado em</dt><dd>{formatLocalDateTime(supplier.homologatedAt)}</dd></div>
      <div><dt className="text-gray-500">Validade</dt><dd>{formatCivilDate(supplier.homologationValidUntil, 'Não informado')}</dd></div>
      <div className="col-span-2"><dt className="text-gray-500">Responsável</dt><dd>{supplier.homologatedByName || 'Não informado'}</dd></div>
    </dl>
    {supplier.legacyHomologationWithoutValidity && (
      <p role="note" className="mt-3 rounded border border-blue-200 bg-blue-50 p-2 text-blue-800">
        Homologação legada sem validade definida. Renove a aprovação final para registrar a vigência.
      </p>
    )}
  </section>
);
